import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, Copy, Loader2, Check, Gift, Percent, Coins,
  CircleDollarSign, ListChecks, Tag, Clock
} from "lucide-react";
import { API } from "@/lib/api";


const REWARD_TYPES = [
  { key: "free_item", label: "Free Item", icon: Gift, hint: "Give away a specific item for free." },
  { key: "item_choice", label: "Item Choice", icon: ListChecks, hint: "Let the customer pick from a list of items." },
  { key: "percent_discount", label: "% Discount", icon: Percent, hint: "A percentage off the order." },
  { key: "cash_value", label: "Cash Value", icon: CircleDollarSign, hint: "A fixed £ amount off." },
  { key: "bonus_points", label: "Bonus Points", icon: Coins, hint: "Award extra points." },
];

const TYPE_META = REWARD_TYPES.reduce((a, t) => { a[t.key] = t; return a; }, {});

const EMPTY_REWARD = {
  name: "", reward_type: "free_item", description: "",
  item_options: [{ label: "", item_id: "" }],
  value_gbp: 0, discount_percent: 0, points_value: 0,
  image_url: "", expiry_days: 365, enabled: true,
};

function valueSummary(r) {
  switch (r.reward_type) {
    case "percent_discount": return `${r.discount_percent || 0}% off`;
    case "cash_value": return `£${(r.value_gbp || 0).toFixed(2)} off`;
    case "bonus_points": return `+${r.points_value || 0} pts`;
    case "item_choice": return `${(r.item_options || []).length} options`;
    default: return r.value_gbp ? `£${r.value_gbp.toFixed(2)} value` : "Free";
  }
}

/* ── Reward Editor Modal ── */
function RewardModal({ open, onClose, reward, onSave, saving }) {
  const [form, setForm] = useState({ ...EMPTY_REWARD });

  useEffect(() => {
    if (open) {
      const base = reward ? JSON.parse(JSON.stringify(reward)) : { ...EMPTY_REWARD };
      if (!base.item_options || base.item_options.length === 0) base.item_options = [{ label: "", item_id: "" }];
      setForm(base);
    }
  }, [open, reward]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setOpt = (i, k, v) => setForm(prev => {
    const opts = [...(prev.item_options || [])];
    opts[i] = { ...opts[i], [k]: v };
    return { ...prev, item_options: opts };
  });
  const addOpt = () => setForm(prev => ({ ...prev, item_options: [...(prev.item_options || []), { label: "", item_id: "" }] }));
  const removeOpt = (i) => setForm(prev => ({ ...prev, item_options: prev.item_options.filter((_, idx) => idx !== i) }));

  const t = form.reward_type;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-ink/5">
          <DialogTitle className="text-xl font-bold text-ink">{reward ? "Edit reward" : "New reward"}</DialogTitle>
          <p className="text-sm text-ink/40">Reusable reward you can attach to stamp milestones, tiers or point thresholds.</p>
        </DialogHeader>
        <div className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Reward Name</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Free regular pizza" className="mt-1 h-10 rounded-xl" data-testid="reward-name" />
          </div>

          {/* Type */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Reward Type</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {REWARD_TYPES.map(rt => {
                const Icon = rt.icon;
                const active = t === rt.key;
                return (
                  <button key={rt.key} type="button" onClick={() => set("reward_type", rt.key)}
                    data-testid={`reward-type-${rt.key}`}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border text-center transition-colors ${active ? "bg-purple-500 text-white border-ink" : "bg-white text-ink/60 border-ink/15 hover:border-ink/30"}`}>
                    <Icon size={16} />
                    <span className="text-[10px] font-medium leading-tight">{rt.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-ink/30 mt-1.5">{TYPE_META[t]?.hint}</p>
          </div>

          {/* Type-specific value fields */}
          {t === "percent_discount" && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Discount %</Label>
              <Input type="number" min="0" max="100" value={form.discount_percent} onChange={e => set("discount_percent", parseFloat(e.target.value) || 0)} className="mt-1 h-10 rounded-xl" data-testid="reward-discount" />
            </div>
          )}
          {(t === "cash_value" || t === "free_item") && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">{t === "cash_value" ? "Cash Value (£)" : "Item Value (£) — for reporting"}</Label>
              <Input type="number" min="0" step="0.01" value={form.value_gbp} onChange={e => set("value_gbp", parseFloat(e.target.value) || 0)} className="mt-1 h-10 rounded-xl" data-testid="reward-value" />
            </div>
          )}
          {t === "bonus_points" && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Bonus Points</Label>
              <Input type="number" min="0" value={form.points_value} onChange={e => set("points_value", parseInt(e.target.value, 10) || 0)} className="mt-1 h-10 rounded-xl" data-testid="reward-points" />
            </div>
          )}
          {t === "item_choice" && (
            <div className="p-4 bg-surface rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Item Options</Label>
                <span className="text-[10px] text-ink/30">Label + your POS / menu item ID</span>
              </div>
              {(form.item_options || []).map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-6 h-9 rounded-lg text-xs" placeholder="Label e.g. Any chicken dish" value={o.label} onChange={e => setOpt(i, "label", e.target.value)} data-testid={`option-label-${i}`} />
                  <Input className="col-span-5 h-9 rounded-lg text-xs font-mono" placeholder="Item ID e.g. SKU-1024" value={o.item_id} onChange={e => setOpt(i, "item_id", e.target.value)} data-testid={`option-id-${i}`} />
                  <button type="button" className="col-span-1 flex justify-center text-ink/30 hover:text-danger" onClick={() => removeOpt(i)}><Trash2 size={14} /></button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addOpt} className="h-7 text-xs gap-1 rounded-full" data-testid="add-option-btn"><Plus size={12} /> Add option</Button>
              <div className="pt-1">
                <Label className="text-[10px] text-ink/40">Item Value (£) — for reporting</Label>
                <Input type="number" min="0" step="0.01" value={form.value_gbp} onChange={e => set("value_gbp", parseFloat(e.target.value) || 0)} className="mt-1 h-9 rounded-lg text-xs" />
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Description</Label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Shown to the customer (optional)" className="mt-1 rounded-xl text-sm" rows={2} data-testid="reward-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Expiry */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Expiry (days)</Label>
              <Input type="number" min="1" value={form.expiry_days} onChange={e => set("expiry_days", parseInt(e.target.value, 10) || 365)} className="mt-1 h-10 rounded-xl" data-testid="reward-expiry" />
            </div>
            {/* Image */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-ink/50">Image URL</Label>
              <Input value={form.image_url || ""} onChange={e => set("image_url", e.target.value)} placeholder="https://… (optional)" className="mt-1 h-10 rounded-xl text-xs" />
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center justify-between p-3 bg-surface rounded-xl">
            <div>
              <p className="text-sm font-medium text-ink">Active</p>
              <p className="text-[10px] text-ink/40">Inactive rewards can't be attached or issued</p>
            </div>
            <Switch checked={form.enabled !== false} onCheckedChange={v => set("enabled", v)} data-testid="reward-enabled" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-ink/5 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => onClose(false)} className="text-ink/60">Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={saving || !form.name.trim()} className="bg-purple-500 hover:bg-purple-600 text-white rounded-full gap-1.5" data-testid="save-reward-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── MAIN PAGE ── */
export default function VendorRewardsPage() {
  const { authHeaders, user } = useAuth();
  const vid = user?.vendor_id;
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editReward, setEditReward] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!vid) return;
    try {
      const { data } = await axios.get(`${API}/rewards/${vid}`, { headers: authHeaders() });
      setRewards(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [vid, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editReward?.id) {
        await axios.put(`${API}/rewards/${vid}/${editReward.id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/rewards/${vid}`, form, { headers: authHeaders() });
      }
      setModalOpen(false);
      setEditReward(null);
      await load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleToggle = async (r) => {
    try {
      await axios.patch(`${API}/rewards/${vid}/${r.id}/toggle`, {}, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete reward "${r.name}"?`)) return;
    try {
      await axios.delete(`${API}/rewards/${vid}/${r.id}`, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
  };

  const handleDuplicate = async (r) => {
    const copy = { ...r, name: `${r.name} (copy)`, enabled: false };
    delete copy.id; delete copy.created_at; delete copy.updated_at; delete copy.vendor_id;
    try {
      await axios.post(`${API}/rewards/${vid}`, copy, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="vendor-rewards-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Rewards Catalog</h1>
          <p className="text-sm text-ink/40 mt-0.5 max-w-2xl">
            Build reusable rewards once — free items, item choices, discounts, cash value or bonus points.
            Attach them to stamp milestones, tiers or point thresholds in <span className="font-medium text-ink/60">Design</span>.
          </p>
        </div>
        <Button onClick={() => { setEditReward(null); setModalOpen(true); }} className="bg-purple-500 hover:bg-purple-600 text-white rounded-full gap-1.5 shrink-0" data-testid="add-reward-btn">
          <Plus size={15} /> New Reward
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 size={18} className="animate-spin text-ink/30" /></div>
      ) : rewards.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-3"><Gift size={20} className="text-ink" /></div>
          <p className="text-sm font-medium text-ink">No rewards yet</p>
          <p className="text-xs text-ink/40 mt-1">Create your first reward to attach it to your loyalty system.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rewards-grid">
          {rewards.map(r => {
            const Icon = TYPE_META[r.reward_type]?.icon || Gift;
            return (
              <div key={r.id} className="bg-white rounded-2xl shadow-sm p-4 flex flex-col group" data-testid={`reward-card-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center shrink-0"><Icon size={18} className="text-ink" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">{r.name}</p>
                    <p className="text-[11px] text-ink/40">{TYPE_META[r.reward_type]?.label}</p>
                  </div>
                  <Switch checked={r.enabled !== false} onCheckedChange={() => handleToggle(r)} data-testid={`reward-toggle-${r.id}`} />
                </div>
                {r.description && <p className="text-xs text-ink/50 mt-2 line-clamp-2">{r.description}</p>}
                <div className="flex items-center gap-3 mt-3 text-[11px] text-ink/50">
                  <span className="inline-flex items-center gap-1 font-medium text-ink/70"><Tag size={11} /> {valueSummary(r)}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {r.expiry_days}d</span>
                </div>
                <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-ink/5">
                  <button onClick={() => handleDuplicate(r)} className="p-1.5 rounded-lg text-ink/25 hover:text-ink hover:bg-purple-50" title="Duplicate"><Copy size={13} /></button>
                  <button onClick={() => { setEditReward(r); setModalOpen(true); }} className="p-1.5 rounded-lg text-ink/25 hover:text-ink hover:bg-purple-50" title="Edit" data-testid={`edit-reward-${r.id}`}><Pencil size={13} /></button>
                  <button onClick={() => handleDelete(r)} className="p-1.5 rounded-lg text-ink/25 hover:text-danger hover:bg-danger/10" title="Delete" data-testid={`delete-reward-${r.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RewardModal open={modalOpen} onClose={setModalOpen} reward={editReward} onSave={handleSave} saving={saving} />
    </div>
  );
}
